import axios from 'axios';

export const NATIVE_SESSION_MODE_HEADER = 'X-Native-Session-Mode';
export const NATIVE_SESSION_MODE_HOST_ONLY_V1 = 'host-only-v1';
export const BROWSER_SESSION_PROTOCOL_COOKIE_ONLY_V1 = 'cookie-only-v1';

const IDP_ORIGIN = process.env.REACT_APP_IDP_ORIGIN || '';
const ACCESS_TOKEN_STORAGE_KEY = 'access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';

let refreshPromise = null;

function usesNativeRefreshBroker() {
    return isInNativeWebView() && (
        window.__CC_NATIVE_CAPABILITIES__?.supportsNativeRefreshBroker === true ||
        window.__CC_NATIVE_CAPABILITIES__?.nativeSessionMode === NATIVE_SESSION_MODE_HOST_ONLY_V1
    );
}

function brokerError(code, status = 503) {
    return Object.assign(new Error('Native session refresh failed'), {
        response: { status, data: { error: code } },
    });
}

function requestNativeAccess() {
    const epoch = getNativeSessionEpoch();
    const bridge = window.ReactNativeWebView;
    if (!Number.isSafeInteger(epoch) || typeof bridge?.postMessage !== 'function') {
        return Promise.reject(brokerError('native_refresh_unavailable'));
    }
    let requestId;
    try {
        requestId = Array.from(window.crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
    } catch {
        return Promise.reject(brokerError('native_refresh_unavailable'));
    }
    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (error, accessToken) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            window.removeEventListener('cc:native-refresh-result', receive);
            if (error) reject(error);
            else {
                persistNativeSession({ access_token: accessToken });
                resolve({ data: { access_token: accessToken } });
            }
        };
        const receive = event => {
            const detail = event.detail;
            if (!detail || detail.requestId !== requestId || detail.epoch !== epoch) return;
            if (getNativeSessionEpoch() !== epoch) return finish(brokerError('native_session_changed'));
            if (detail.error) {
                const expired = ['session_unavailable', 'invalid_or_expired', 'token_reused', 'fingerprint_mismatch'].includes(detail.error);
                return finish(brokerError(detail.error, expired ? 401 : 503));
            }
            if (typeof detail.accessToken !== 'string' || !detail.accessToken.trim() || detail.accessToken.length > 16384) return finish(brokerError('native_refresh_failed'));
            finish(null, detail.accessToken);
        };
        const timer = window.setTimeout(() => finish(brokerError('native_refresh_timeout')), 30000);
        window.addEventListener('cc:native-refresh-result', receive);
        try {
            bridge.postMessage(JSON.stringify({ v: 1, type: 'native_refresh_request', data: { requestId, src: 'support_refresh' }, epoch }));
        } catch {
            finish(brokerError('native_refresh_unavailable'));
        }
    });
}

function readSessionValue(key) {
    try {
        return typeof window !== 'undefined' && window.sessionStorage
            ? String(window.sessionStorage.getItem(key) || '').trim()
            : '';
    } catch {
        return '';
    }
}

function writeSessionValue(key, value) {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return;
        const normalized = typeof value === 'string' ? value.trim() : '';
        if (normalized) window.sessionStorage.setItem(key, normalized);
        else window.sessionStorage.removeItem(key);
    } catch {
        // Storage may be unavailable in hardened WebViews.
    }
}

export function isInNativeWebView() {
    try {
        if (typeof window === 'undefined') return false;
        return Boolean(
            window.ReactNativeWebView ||
            window.__CC_NATIVE_CAPABILITIES__ ||
            window.__CC_NATIVE_SESSION_CONTEXT__
        );
    } catch {
        return false;
    }
}

export function getNativeSessionModeHeaders() {
    try {
        return window.__CC_NATIVE_CAPABILITIES__?.nativeSessionMode === NATIVE_SESSION_MODE_HOST_ONLY_V1
            ? { [NATIVE_SESSION_MODE_HEADER]: NATIVE_SESSION_MODE_HOST_ONLY_V1 }
            : {};
    } catch {
        return {};
    }
}

export function getBrowserSessionProtocolPayload() {
    return isInNativeWebView()
        ? {}
        : { session_protocol: BROWSER_SESSION_PROTOCOL_COOKIE_ONLY_V1 };
}

export function getStoredNativeAccessToken() {
    return isInNativeWebView() ? readSessionValue(ACCESS_TOKEN_STORAGE_KEY) : '';
}

export function getStoredNativeRefreshToken() {
    return isInNativeWebView() && !usesNativeRefreshBroker() ? readSessionValue(REFRESH_TOKEN_STORAGE_KEY) : '';
}

export function persistNativeSession(payload = {}) {
    if (!isInNativeWebView()) return;
    const accessToken = payload?.access_token || payload?.token;
    if (typeof accessToken === 'string' && accessToken.trim()) {
        writeSessionValue(ACCESS_TOKEN_STORAGE_KEY, accessToken);
    }
    if (usesNativeRefreshBroker()) {
        writeSessionValue(REFRESH_TOKEN_STORAGE_KEY, '');
    } else if (typeof payload?.refresh_token === 'string' && payload.refresh_token.trim()) {
        writeSessionValue(REFRESH_TOKEN_STORAGE_KEY, payload.refresh_token);
    }
}

function getNativeSessionEpoch() {
    try {
        const raw = window.__CC_NATIVE_SESSION_CONTEXT__?.sessionEpoch;
        return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0
            ? Math.floor(raw)
            : undefined;
    } catch {
        return undefined;
    }
}

export function exportSessionToNative(payload) {
    try {
        if (usesNativeRefreshBroker()) return false;
        const bridge = window.ReactNativeWebView;
        if (!isInNativeWebView() || !bridge || typeof bridge.postMessage !== 'function') return false;
        if (!payload?.access_token && !payload?.refresh_token) return false;

        const epoch = getNativeSessionEpoch();
        bridge.postMessage(JSON.stringify({
            v: 1,
            type: 'refresh_export_inline',
            data: payload,
            ...(epoch == null ? {} : { epoch }),
        }));
        return true;
    } catch {
        return false;
    }
}

export function buildNativeIdHeaders(fingerprint, options = {}) {
    const headers = {};
    if (fingerprint) headers['X-CSRF-Token'] = fingerprint;
    if (!isInNativeWebView()) return headers;

    headers['X-Client'] = 'mobile';
    Object.assign(headers, getNativeSessionModeHeaders());

    if (options.includeRefreshToken) {
        const refreshToken = getStoredNativeRefreshToken();
        if (refreshToken) headers['X-Refresh-Token'] = refreshToken;
    }
    if (options.includeAccessToken) {
        const accessToken = getStoredNativeAccessToken();
        if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    }

    return headers;
}

export function refreshNativeSession(fingerprint) {
    if (refreshPromise) return refreshPromise;

    if (usesNativeRefreshBroker()) {
        refreshPromise = requestNativeAccess().finally(() => { refreshPromise = null; });
        return refreshPromise;
    }

    const refreshToken = getStoredNativeRefreshToken();
    const headers = buildNativeIdHeaders(fingerprint, { includeRefreshToken: true });
    const payload = refreshToken
        ? { refresh_token: refreshToken }
        : getBrowserSessionProtocolPayload();

    refreshPromise = axios
        .post(
            `${IDP_ORIGIN}/api/refresh`,
            payload,
            { withCredentials: true, headers }
        )
        .then((response) => {
            persistNativeSession(response?.data || {});
            exportSessionToNative({
                access_token: response?.data?.access_token || response?.data?.token || '',
                refresh_token: response?.data?.refresh_token || refreshToken,
                fp: fingerprint || '',
                src: 'support_refresh',
            });
            return response;
        })
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
}
